import React from 'react';
import { IoMdClose } from 'react-icons/io';

const FormulasExplanation = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Formulas Used in Calculations</h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 cursor-pointer"
            aria-label="Close"
          >
            <IoMdClose size={24} />
          </button>
        </div>
        
        <div className="p-6 text-gray-800">
          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Standard Repayment Formula</h3>
            <p className="mb-4 text-gray-800">The monthly payment for a loan is calculated using the following formula:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm overflow-x-auto text-gray-800">
              <p className="mb-2">Monthly Payment = P × (r × (1 + r)^n) / ((1 + r)^n - 1)</p>
              <p>Where:</p>
              <ul className="list-disc list-inside ml-4">
                <li>P = Principal (loan amount)</li>
                <li>r = Monthly interest rate (annual rate ÷ 12 ÷ 100)</li>
                <li>n = Total number of payments (term in months)</li>
              </ul>
            </div>
            <p className="mb-4 text-gray-800">For loans with 0% interest, the formula simplifies to:</p>
            <div className="p-4 bg-gray-50 rounded-md font-mono text-sm text-gray-800">
              <p>Monthly Payment = Principal ÷ Number of Payments</p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Total Interest Calculation</h3>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>Total Interest = (Monthly Payment × Number of Payments) - Principal</p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">APR Calculation</h3>
            <p className="mb-4 text-gray-800">The Annual Percentage Rate (APR) is calculated as:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm overflow-x-auto text-gray-800">
              <p className="mb-2">APR = ((1 + r/12)^12 - 1) × 100</p>
              <p>Where:</p>
              <ul className="list-disc list-inside ml-4">
                <li>r = Annual interest rate (as a decimal)</li>
              </ul>
            </div>
            <p className="text-sm text-gray-600 italic">
              This formula converts the nominal interest rate to an effective annual rate, accounting for compounding interest.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Effective APR Calculation</h3>
            <p className="mb-4 text-gray-800">When a loan has missed payments, late fees, or other adjustments, we calculate the effective APR:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm overflow-x-auto text-gray-800">
              <p className="mb-2">Effective APR = (Total Interest ÷ (Principal × Term in Years)) × 100</p>
              <p>Where:</p>
              <ul className="list-disc list-inside ml-4">
                <li>Total Interest = Total Repaid - Principal</li>
                <li>Term in Years = Term in Months ÷ 12</li>
              </ul>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Missed Payment Calculations</h3>
            <p className="mb-4 text-gray-800">When payments are missed, interest continues to accrue and is capitalized into the balance:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>New Balance = Previous Balance + Accrued Interest + Late Fees</p>
              <p>Monthly Payment Amount = Unchanged</p>
              <p>Extended Term = Required to repay the larger balance</p>
            </div>
            <p className="mb-4 text-gray-800">The impact compounds because:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>Capitalized Interest = Outstanding Balance × Monthly Rate</p>
              <p>Late Fees = Number of Missed Payments × Late Fee per Payment</p>
              <p>Future Interest = Calculated on the increased balance</p>
            </div>
            <p className="text-sm text-gray-600 italic">
              This approach realistically models how interest capitalizes during missed payment periods, leading to a larger outstanding balance that requires additional months to repay.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Interest Freeze Calculations</h3>
            <p className="mb-4 text-gray-800">During an interest freeze period:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>Entire Payment = Principal Reduction (Interest = 0)</p>
            </div>
            <p className="text-sm text-gray-600 italic">
              This accelerates loan repayment as the entire payment goes toward reducing the principal during the freeze period.
            </p>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Repayment Restructuring Calculations</h3>
            <p className="mb-4 text-gray-800">1. Spreading arrears over remaining term:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>New Balance = Current Balance + Arrears</p>
              <p>New Monthly Payment = calculated using standard formula with New Balance</p>
            </div>

            <p className="mb-4 text-gray-800">2. Calculating term extension (solving for n):</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm overflow-x-auto text-gray-800">
              <p className="mb-2">n = -log(1 - (P × r)/p) ÷ log(1 + r)</p>
              <p>Where:</p>
              <ul className="list-disc list-inside ml-4">
                <li>P = Principal (new balance including arrears)</li>
                <li>r = Monthly interest rate</li>
                <li>p = Monthly payment amount</li>
                <li>n = New number of payments needed</li>
              </ul>
            </div>

            <p className="mb-4 text-gray-800">3. Reduced payment calculations:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>Minimum Payment Required = Current Balance × Monthly Interest Rate</p>
              <p>New Term = calculated using the term extension formula with reduced payment</p>
            </div>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold mb-3 text-blue-700 border-b pb-2">Amortization Schedule Calculation</h3>
            <p className="mb-4 text-gray-800">For each payment in the schedule:</p>
            <div className="p-4 bg-gray-50 rounded-md mb-4 font-mono text-sm text-gray-800">
              <p>Interest Payment = Remaining Balance × Monthly Interest Rate</p>
              <p>Principal Payment = Monthly Payment - Interest Payment</p>
              <p>New Remaining Balance = Previous Balance - Principal Payment</p>
            </div>
          </div>

          <div>
            <p className="text-sm text-gray-600 italic">
              Note: All calculations use high-precision decimal arithmetic to minimize rounding errors and ensure accurate results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormulasExplanation;